import React, { forwardRef, ReactNode, useCallback, useEffect, useState } from 'react';

import { LabelDisplayedRowsArgs } from '@mui/material';
import { deepmerge } from '@mui/utils';
import {
    DataGridPro,
    GridApiPro,
    GridCallbackDetails,
    GridColDef,
    GridColumnGroupingModel,
    GridPinnedColumnFields,
    GridRowId,
    GridRowParams,
    GridRowSelectionModel,
    GridRowsProp,
} from '@mui/x-data-grid-pro';

import { Centered } from 'components/grid';
import { CustomFooter } from 'components/grid/components/CustomFooter';
import { CustomNoRowsOverlay } from 'components/grid/components/CustomNoRowsOverlay';
import { useListagemContext } from 'context/ListagemContext';
import { uniqueArray } from 'util/format';

interface PanelGridProps {
    rows: GridRowsProp;
    rowsCount?: number;
    columns: GridColDef[];
    summary?: string[];
    summaryPrefix?: string | boolean;
    loading: boolean;
    reload: false | (() => void);
    remote?: boolean;
    visibleRows?: number | 'all';
    columnGrouping?: GridColumnGroupingModel;
    onClickRow?: (selectionModel: GridRowSelectionModel, details: GridCallbackDetails<any>) => void;
    pinnedColumns?: GridPinnedColumnFields;
    columnBorder?: boolean;
    rowContent?: (params: GridRowParams<any>) => React.ReactNode;
    rowContentHeight?: number | 'auto';
    onRowContentExpandedChange?: (ids: Set<GridRowId>, details: GridCallbackDetails<any>) => void;
    hideFooter?: boolean;
    customMessageNoRows?: string;
    rowThreshold?: number | string;
    getDetailPanelHeight?: (params: GridRowParams) => number | 'auto';
    getDetailPanelContent?: (params: GridRowParams) => ReactNode;
    isTreeData?: boolean;
    rowCellHeight?: 'auto';
    checkboxSelection?: boolean;
    selectionModel?: GridRowSelectionModel;
    keepNonExistentRowsSelected?: boolean;
    autoHeight?: boolean;
    disableSelectionOnClick?: boolean;
    hasNext?: boolean; // NOVO: indica se há próxima página
}

const rowsPerPageOptions = [12, 24, 36, 48];

export const PanelGrid = forwardRef<GridApiPro, PanelGridProps>(
    (
        {
            rows,
            rowsCount = 0,
            columns,
            loading,
            reload,
            summary = [],
            summaryPrefix,
            remote = true,
            visibleRows,
            columnGrouping,
            onClickRow,
            pinnedColumns,
            columnBorder,
            rowContent,
            rowContentHeight = 500, // default do MUI = 500
            onRowContentExpandedChange,
            hideFooter,
            customMessageNoRows,
            isTreeData = false,
            getDetailPanelHeight,
            getDetailPanelContent,
            rowCellHeight,
            checkboxSelection = false,
            selectionModel,
            keepNonExistentRowsSelected = false,
            autoHeight = true,
            disableSelectionOnClick = true,
            hasNext = false,
        },
        ref,
    ) => {
        const { limit, autoLoad, setPage, page, setSort, setLimit } = useListagemContext();
        const [gridRows, setGridRows] = useState(rows);
        const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(new Set());
        const [gridRowsCount, setGridRowsCount] = useState(rowsCount);

        const experimentalFeatures = { columnGrouping: false };
        const mode = remote ? 'server' : 'client';
        const hideFooterSelectedRowCount = typeof visibleRows !== 'undefined';
        const hideTotalCount = rowsCount === -1;

        let pageSize = limit;

        if (typeof visibleRows === 'string') {
            pageSize = rowsCount;
        } else if (typeof visibleRows === 'number') {
            pageSize = visibleRows;
        }

        if (columnGrouping) {
            experimentalFeatures.columnGrouping = true;
        }

        if (!rowsPerPageOptions.includes(pageSize)) {
            rowsPerPageOptions.push(pageSize);
        }

        const handlePageChange = useCallback(
            (newPage: number): void => {
                const page = newPage + 1;
                setPage(page);
            },
            [setPage],
        );

        const handleDetailPanelExpandedRowIdsChange = useCallback(
            (newIds: Set<GridRowId>, details: GridCallbackDetails<any>) => {
                if (newIds.size > 1) {
                    const lastId = Array.from(newIds).at(-1);

                    if (lastId) {
                        setExpandedRowIds(new Set([lastId]));
                    }
                } else {
                    setExpandedRowIds(new Set(newIds));
                }

                if (typeof onRowContentExpandedChange === 'function') {
                    onRowContentExpandedChange(newIds, details);
                }
            },
            [onRowContentExpandedChange],
        );

        useEffect(() => {
            if (!loading) {
                setGridRows(rows);

                if (hideTotalCount) {
                    const currentPageRows = page * pageSize;

                    // abordagem para quando não sabe o total de registros
                    // se hasNext for true, adiciona +1 página ao total atual
                    // e o componente entende que há mais registros para carregar
                    setGridRowsCount(hasNext ? currentPageRows + 1 : currentPageRows);
                } else {
                    setGridRowsCount(rowsCount);
                }
            }
        }, [loading, rows, rowsCount, hasNext, page, pageSize, hideTotalCount]);

        const CustomMessageNoRows = () => <Centered>{customMessageNoRows}</Centered>;

        return (
            <DataGridPro
                disableRowSelectionExcludeModel
                apiRef={ref as React.MutableRefObject<GridApiPro>}
                rows={gridRows}
                rowCount={remote ? gridRowsCount : undefined}
                columns={columns}
                autoHeight={autoHeight}
                loading={loading}
                paginationMode={mode}
                pagination
                pageSizeOptions={hideFooterSelectedRowCount ? [pageSize] : uniqueArray<number>(rowsPerPageOptions.sort((a, b) => a - b))}
                hideFooterSelectedRowCount={hideFooterSelectedRowCount}
                paginationModel={{ page: page - 1, pageSize }}
                onPaginationModelChange={(newPageSize) => {
                    setLimit(newPageSize.pageSize);
                    handlePageChange(newPageSize.page);
                }}
                onRowSelectionModelChange={onClickRow}
                rowSelectionModel={selectionModel}
                columnGroupingModel={columnGrouping}
                pinnedColumns={pinnedColumns}
                disableColumnFilter
                sortingMode={mode}
                onSortModelChange={(sortModel) => setSort(sortModel)}
                slots={{
                    noRowsOverlay: customMessageNoRows ? CustomMessageNoRows : CustomNoRowsOverlay,
                    footer: CustomFooter,
                }}
                slotProps={{
                    loadingOverlay: {
                        variant: 'circular-progress',
                        noRowsVariant: 'circular-progress',
                    },
                    pagination: {
                        labelDisplayedRows: ({ from, to, count }: LabelDisplayedRowsArgs) => {
                            if (hideTotalCount) {
                                return `Página ${page}`;
                            }

                            return `${from} - ${to} de ${count}`;
                        },
                    },
                    footer: {
                        refresh: reload,
                        summary,
                        summaryPrefix,
                        hideFooter,
                    },
                    noRowsOverlay: {
                        started: autoLoad,
                    },
                }}
                getRowHeight={() => (rowCellHeight ? 'auto' : null)}
                getRowClassName={(params) => {
                    const { featured, fgAtivo } = params.row;

                    if (featured) {
                        return 'mui-personalized-row-featured';
                    }

                    if (typeof fgAtivo === 'boolean' && fgAtivo === false) {
                        return 'mui-personalized-row-disabled';
                    }

                    return '';
                }}
                sx={deepmerge(
                    {
                        'backgroundColor': 'common.white',
                        '& .mui-personalized-row-featured': {
                            'backgroundColor': (theme) => theme.alpha(theme.palette.primary.main, 0.25),

                            '&:hover': {
                                backgroundColor: (theme) => theme.alpha(theme.palette.primary.main, 0.45),
                            },
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            textOverflow: 'clip',
                            whiteSpace: 'break-spaces',
                            lineHeight: 1,
                        },
                        '& .mui-personalized-row-disabled': {
                            'opacity': 0.3,

                            '&:hover': {
                                opacity: 1,
                            },
                        },
                        '& .MuiDataGrid-columnHeader--alignRight': {
                            textAlign: 'right',
                        },
                        '& .MuiDataGrid-cell:has(*)': {
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-cell .MuiAutocomplete-root': {
                            display: 'flex',
                            alignItems: 'center',
                        },
                    },
                    columnBorder && {
                        '& .MuiDataGrid-cell': {
                            borderRight: '1px solid #efefef',
                        },
                    },
                )}
                getDetailPanelContent={isTreeData ? getDetailPanelContent : rowContent}
                getDetailPanelHeight={isTreeData ? getDetailPanelHeight : () => rowContentHeight}
                onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                detailPanelExpandedRowIds={expandedRowIds}
                checkboxSelection={checkboxSelection}
                disableRowSelectionOnClick={disableSelectionOnClick}
                keepNonExistentRowsSelected={keepNonExistentRowsSelected}
            />
        );
    },
);
